import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Crown, Download, Loader2, Orbit, Clock, ArrowRight, Lock, RefreshCw, ChevronDown, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getKarmicUsage, getKarmicPreview, generateKarmicReport,
  listKarmicReports, getKarmicReport, downloadKarmicPdf, createCheckout, getUserBirthData,
} from "@/lib/api";
import { ReportDetailsForm } from "@/components/ReportDetailsForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";

const SIGN_GLYPH = {
  Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
  Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso?.slice(0, 10) || ""; }
};

async function readErrorDetail(e) {
  let data = e?.response?.data;
  if (data instanceof Blob) { try { data = JSON.parse(await data.text()); } catch { return null; } }
  return data?.detail ?? null;
}

const SECTION_DEFS = [
  ["focus", "Your Karmic Focus"],
  ["natal_pattern", "Your Natal Nodal Pattern"],
  ["current_axis", "Your Current Axis"],
  ["coming_into_focus", "What May Be Coming Into Focus"],
  ["upcoming_axis", "Your Upcoming Axis"],
];

// Lightweight renderer: paragraphs (blank-line separated), bullets ("- " / "•"), inline **bold**
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-white/90 font-medium">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function RichText({ text, className = "" }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let para = [];
  let bullets = [];
  const flushPara = () => { if (para.length) { blocks.push({ type: "p", content: para.join(" ") }); para = []; } };
  const flushBullets = () => { if (bullets.length) { blocks.push({ type: "ul", items: [...bullets] }); bullets = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushBullets(); continue; }
    if (/^(-|•|\*)\s+/.test(line)) { flushPara(); bullets.push(line.replace(/^(-|•|\*)\s+/, "")); }
    else { flushBullets(); para.push(line); }
  }
  flushPara(); flushBullets();
  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((b, i) =>
        b.type === "p"
          ? <p key={i} className="text-white/70 text-[15px] leading-7">{renderInline(b.content)}</p>
          : <ul key={i} className="space-y-2">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-2.5 text-white/70 text-[15px] leading-7">
                  <span className="text-amber-400/60 mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-amber-400/60" />
                  <span>{renderInline(it)}</span>
                </li>
              ))}
            </ul>
      )}
    </div>
  );
}

const CARD_DEFS = [
  ["explore", "Explore", "#f59e0b", "Rahu", "rahu"],
  ["release", "Release", "#a78bfa", "Ketu", "ketu"],
  ["practice", "Practice", "#60a5fa", "Saturn", "saturn"],
  ["develop", "Develop", "#34d399", "Jupiter", "jupiter"],
];
const PLANET_COLOR = {
  rahu: "#f59e0b", ketu: "#a78bfa", saturn: "#60a5fa", jupiter: "#34d399", dasha: "#fbbf24",
};
const TL_SECTIONS = [
  ["rahu", "What is drawing you forward?", "Rahu"],
  ["ketu", "What are you ready to release?", "Ketu"],
  ["saturn", "What needs commitment?", "Saturn"],
  ["jupiter", "Where can you find support and growth?", "Jupiter"],
  ["dasha", "Why is this relevant now?", "Your current dasha"],
];
const MILESTONE_COLOR = {
  "Nodal axis shift": "#a78bfa", "Slow-planet sign change": "#f59e0b",
  "Dasha subperiod": "#34d399", "Dasha period": "#34d399", "Transit contact": "#60a5fa",
};

function MilestoneTimeline({ data }) {
  const start = new Date(data.window.start).getTime();
  const end = new Date(data.window.end).getTime();
  const span = end - start || 1;
  const pct = (iso) => Math.min(98, Math.max(2, ((new Date(iso).getTime() - start) / span) * 100));
  const ms = data.milestones || [];
  return (
    <div data-testid="karmic-timeline">
      <div className="relative h-10 mb-5">
        <div className="absolute top-4 left-0 right-0 h-px bg-white/15" />
        <div className="absolute top-2.5 left-0 flex flex-col items-start">
          <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
          <span className="text-white/40 text-[10px] mt-1">{fmtDate(data.window.start)}</span>
        </div>
        {ms.map((m, i) => (
          <div key={i} className="absolute top-3 -translate-x-1/2 group" style={{ left: `${pct(m.date)}%` }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: MILESTONE_COLOR[m.category] || "#999" }} title={m.factor} />
          </div>
        ))}
        <div className="absolute top-2.5 right-0 flex flex-col items-end">
          <div className="w-2.5 h-2.5 rounded-full bg-white/40" />
          <span className="text-white/40 text-[10px] mt-1">{fmtDate(data.window.end)}</span>
        </div>
      </div>
      <div className="space-y-2.5">
        {ms.map((m, i) => (
          <div key={i} className="flex gap-3 rounded-lg p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`karmic-milestone-${i}`}>
            <div className="flex-shrink-0 w-2.5 h-2.5 rounded-full mt-1.5" style={{ background: MILESTONE_COLOR[m.category] || "#999" }} />
            <div className="min-w-0">
              <p className="text-white/85 text-sm">{m.factor}</p>
              <p className="text-white/40 text-xs mt-0.5">{fmtDate(m.date)} · {m.category}{m.natal_connection ? ` · ${m.natal_connection}` : ""}</p>
            </div>
          </div>
        ))}
        {ms.length === 0 && <p className="text-white/40 text-sm">No major calculated transitions fall within this 18-month window — a steadier chapter.</p>}
      </div>
    </div>
  );
}

function TimelineSection({ sectionKey, title, planet, text, why, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div id={`karmic-sec-${sectionKey}`} data-testid={`karmic-section-${sectionKey}`} className="pt-1 scroll-mt-20">
      <h3 className="text-white/90 text-base font-light mb-1 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
        {title}
      </h3>
      <p className="text-xs tracking-[0.15em] uppercase mb-3" style={{ color }}>{planet}</p>
      <RichText text={text} />
      {why ? (
        <div className="mt-3">
          <button onClick={() => setOpen(!open)} className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1" data-testid={`karmic-why-${sectionKey}`}>
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} /> Why this appears in your timeline
          </button>
          {open && <p className="text-white/45 text-xs leading-relaxed mt-2 pl-4 border-l border-white/10">{why}</p>}
        </div>
      ) : null}
    </div>
  );
}

function NextStepCard({ step, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`karmic-step-${index}`}>
      {step.title && <p className="text-white/90 text-sm font-medium">{step.title}</p>}
      {step.action && <p className="text-white/70 text-[15px] leading-7 mt-1">{step.action}</p>}
      {step.example && <p className="text-white/50 text-sm leading-6 italic mt-2">For example: {step.example}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs">
        {step.time && <span className="inline-flex items-center gap-1 text-white/45"><Clock className="w-3 h-3" /> {step.time}</span>}
        {step.window && <span className="inline-flex items-center gap-1 text-amber-300/80" data-testid={`karmic-step-window-${index}`}><Calendar className="w-3 h-3" /> Suggested window: {step.window}</span>}
        {step.done_when && <span className="inline-flex items-center gap-1 text-emerald-300/70"><Check className="w-3 h-3" /> Done when: {step.done_when}</span>}
      </div>
      {step.why && (
        <div className="mt-3">
          <button onClick={() => setOpen(!open)} className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1" data-testid={`karmic-step-why-${index}`}>
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} /> Why this is suggested
          </button>
          {open && <p className="text-white/45 text-xs leading-relaxed mt-2 pl-4 border-l border-white/10">{step.why}</p>}
        </div>
      )}
    </div>
  );
}

function KarmicReportView({ report, onDownload, downloading }) {
  const nar = report.narrative || {};
  const td = report.timeline_data;
  const isTimeline = !!(nar.sections && td);

  const header = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-2xl font-light text-white/95">Karmic Timeline — {report.name}</h2>
        <p className="text-white/40 text-sm">Understand your current chapter—and what's unfolding next · {fmtDate(report.window_start)} – {fmtDate(report.window_end)}</p>
      </div>
      <Button onClick={onDownload} disabled={downloading} className="bg-white text-black hover:bg-white/90 rounded-full h-9 px-4 text-xs" data-testid="karmic-download-pdf-btn">
        {downloading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Preparing…</> : <><Download className="w-3.5 h-3.5 mr-1.5" />Download PDF</>}
      </Button>
    </div>
  );

  // Legacy sidereal reports (generated before the Karmic Timeline update)
  if (!isTimeline) {
    return (
      <div className="space-y-6" data-testid="karmic-report-view">
        {header}
        <p className="text-white/40 text-xs italic">An earlier version of this report. Generate a new one for the full Karmic Timeline.</p>
        {["focus", "natal_pattern", "current_axis", "coming_into_focus", "upcoming_axis", "timeline_intro"].map((k) => (
          nar[k] ? <div key={k}><RichText text={nar[k]} /></div> : null
        ))}
        {report.methodology && <p className="text-white/40 text-xs leading-relaxed">{report.methodology}</p>}
      </div>
    );
  }

  const kp = td.key_placements || {};
  const md = td.dasha?.current_mahadasha || {};
  return (
    <div className="space-y-8" data-testid="karmic-report-view">
      {header}

      {/* Overview cards */}
      <div data-testid="karmic-overview-cards">
        <p className="text-white/40 text-xs leading-relaxed mb-3">Your 18-month themes map to four developmental forces — tap a card to jump to its section: <span className="text-amber-300/90">Explore (Rahu)</span>, <span className="text-purple-300/90">Release (Ketu)</span>, <span className="text-blue-300/90">Practice (Saturn)</span>, and <span className="text-emerald-300/90">Develop (Jupiter)</span>.</p>
        <div className="grid grid-cols-2 gap-3">
          {CARD_DEFS.map(([k, label, color, planet, secKey]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                const el = document.getElementById(`karmic-sec-${secKey}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="text-left rounded-xl p-4 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group focus:outline-none focus:ring-1"
              style={{ background: `${color}0f`, border: `1px solid ${color}33`, "--tw-ring-color": `${color}66` }}
              data-testid={`karmic-card-${k}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-xs tracking-[0.2em] uppercase" style={{ color }}>{label}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/35">{planet}</p>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">{nar.cards?.[k]}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-[11px] opacity-60 group-hover:opacity-100 transition-opacity" style={{ color }}>View {planet} <ArrowRight className="w-3 h-3" /></span>
            </button>
          ))}
        </div>
      </div>

      {/* Key placements strip */}
      <div className="rounded-xl p-4 flex flex-wrap gap-x-5 gap-y-2" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="karmic-placements">
        {["Rahu", "Ketu", "Saturn", "Jupiter"].map((n) => kp[n] ? (
          <div key={n} className="text-xs">
            <span className="text-white/40">{n}: </span>
            <span className="text-white/80">{SIGN_GLYPH[kp[n].sign] || ""} {kp[n].sign}{kp[n].house ? ` · H${kp[n].house}` : ""}{kp[n].dignity && kp[n].dignity !== "neutral" ? ` · ${kp[n].dignity}` : ""}</span>
          </div>
        ) : null)}
        <div className="text-xs"><span className="text-white/40">Dasha: </span><span className="text-white/80">{md.planet} MD</span></div>
      </div>

      {/* Chronological timeline (below the overview/placements) */}
      <div data-testid="karmic-section-timeline">
        <h3 className="text-amber-400/90 text-xs tracking-[0.2em] uppercase mb-3 flex items-center gap-2"><span className="w-5 h-px bg-amber-400/40" />Your 18-Month Timeline</h3>
        {nar.timeline_intro && <p className="text-white/70 text-sm leading-relaxed mb-3">{nar.timeline_intro}</p>}
        <MilestoneTimeline data={td} />
      </div>

      {/* Five sections */}
      {TL_SECTIONS.map(([k, title, planet]) => (
        nar.sections?.[k] ? <TimelineSection key={k} sectionKey={k} title={title} planet={planet} text={nar.sections[k]} why={nar.why?.[k]} color={PLANET_COLOR[k]} /> : null
      ))}

      {/* Actions + Journal prompts */}
      {/* Your Next Steps */}
      {(nar.next_steps?.length > 0 || nar.actions?.length > 0) && (
        <div data-testid="karmic-next-steps">
          <h3 className="text-amber-400/90 text-xs tracking-[0.2em] uppercase mb-3 flex items-center gap-2"><span className="w-5 h-px bg-amber-400/40" />Your Next Steps</h3>
          <p className="text-white/50 text-sm leading-relaxed mb-4">Small, optional steps for the next 90 days. Start with whichever step fits your life right now.</p>
          {nar.next_steps?.length > 0 ? (
            <div className="space-y-3">
              {nar.next_steps.map((s, i) => <NextStepCard key={i} step={s} index={i} />)}
            </div>
          ) : (
            <ul className="space-y-2">
              {nar.actions.map((a, i) => (
                <li key={i} className="flex gap-2.5 text-white/70 text-[15px] leading-7"><span className="text-amber-400/60 mt-2 w-1 h-1 rounded-full bg-amber-400/60 flex-shrink-0" /><span>{a}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
      {(nar.questions?.length > 0 || nar.journal_prompts?.length > 0) && (
        <div data-testid="karmic-prompts">
          <h3 className="text-amber-400/90 text-xs tracking-[0.2em] uppercase mb-3 flex items-center gap-2"><span className="w-5 h-px bg-amber-400/40" />Questions to Sit With</h3>
          <ul className="space-y-2">
            {(nar.questions?.length > 0 ? nar.questions : nar.journal_prompts).map((p, i) => (
              <li key={i} className="flex gap-2.5 text-white/70 text-[15px] leading-7 italic"><span className="text-white/30 mt-2 w-1 h-1 rounded-full bg-white/30 flex-shrink-0" /><span>{p}</span></li>
            ))}
          </ul>
        </div>
      )}

      <details className="rounded-lg p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="karmic-methodology">
        <summary className="text-white/50 text-xs tracking-[0.15em] uppercase cursor-pointer">Methodology & Calculation Settings</summary>
        <p className="text-white/40 text-xs leading-relaxed mt-3">{report.methodology}</p>
      </details>
    </div>
  );
}

export default function KarmicAccessReport() {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [usage, setUsage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saved, setSaved] = useState([]);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [needsDetails, setNeedsDetails] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [prefill, setPrefill] = useState({});

  const sessionId = searchParams.get("session_id");
  const openReportId = searchParams.get("report");

  const refresh = useCallback(() => {
    if (!user) return;
    getKarmicUsage().then(setUsage).catch(() => {});
    listKarmicReports().then((d) => setSaved(d.reports || [])).catch(() => {});
  }, [user]);

  useEffect(() => { getKarmicPreview().then(setPreview).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (user) getUserBirthData().then((res) => {
      const d = res?.data || res || {};
      setPrefill({
        name: user.name || d.name || "", birth_date: d.birth_date || "",
        birth_time: d.birth_time || "", birth_city: d.birth_city || "",
      });
    }).catch(() => {});
  }, [user]);

  // Open a previously saved report (free, no allowance)
  useEffect(() => {
    if (openReportId && user) {
      getKarmicReport(openReportId).then((d) => setReport(d.report)).catch(() => toast.error("Could not open that report"));
    }
  }, [openReportId, user]);

  const doGenerate = useCallback(async (extra = {}) => {
    setGenerating(true);
    try {
      const d = await generateKarmicReport({ session_id: sessionId || undefined, ...extra });
      setReport(d.report);
      setNeedsDetails(false);
      toast.success("Your Karmic Timeline is ready");
      refresh();
    } catch (e) {
      const detail = await readErrorDetail(e);
      if (detail?.requires_birth_details) {
        setNeedsDetails(true);
        toast.info("Enter your birth details to generate the report");
      } else if (detail?.requires_payment) {
        setCheckoutFor({ package_id: detail.package_id, price: detail.price });
        toast.info("This report requires payment");
      } else {
        toast.error(detail?.message || "Could not generate the report. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  }, [sessionId, refresh]);

  // After a paid checkout returns with ?session_id=, auto-generate using the paid session
  useEffect(() => {
    if (sessionId && user && !report && !generating) {
      doGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user]);

  const startCheckout = async (form) => {
    const pkg = checkoutFor?.package_id || (usage?.is_premium ? "karmic_access_report_premium" : "karmic_access_report");
    try {
      const checkout = await createCheckout({
        package_id: pkg,
        origin_url: window.location.origin,
        metadata: { ...form, return_to: "karmic" },
      });
      if (checkout.url) window.location.href = checkout.url;
      else throw new Error("no url");
    } catch {
      toast.error("Failed to start checkout");
    }
  };

  const handleGenerateClick = () => {
    if (usage?.can_generate_free) {
      setEditOpen(true); // let the user review/edit birth info before generating
    } else {
      setCheckoutFor({ package_id: usage?.is_premium ? "karmic_access_report_premium" : "karmic_access_report", price: usage?.price_per_report });
    }
  };

  const downloadPdf = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const blob = await downloadKarmicPdf(report.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Karmic_Timeline_Report_${report.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
    finally { setDownloading(false); }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><img src="/logo.png" alt="" className="h-12 w-auto logo-pulse" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center px-6">
          <Orbit className="w-10 h-10 text-amber-400/70 mx-auto mb-4" />
          <h1 className="text-3xl font-light text-white/90 mb-3">Karmic Timeline</h1>
          <p className="text-white/40 text-sm mb-6 max-w-sm">Understand your current chapter—and what's unfolding next. A personalized, rolling 18-month overview of your developmental themes using tropical Vedic astrology.</p>
          <Button onClick={loginWithGoogle} className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-11 text-sm" data-testid="karmic-login-btn">Sign in with Google</Button>
          <div className="mt-4"><Link to="/" className="text-white/30 text-sm hover:text-white/50">Back to Dashboard</Link></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="karmic-back-link">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm tracking-wider uppercase">Dashboard</span>
          </Link>
          <Link to="/my-reports" className="text-white/50 hover:text-white/80 text-sm" data-testid="karmic-myreports-link">My Reports</Link>
        </div>
      </nav>

      <main className="pt-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {report ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <button onClick={() => { setReport(null); navigate("/karmic"); }} className="text-white/40 hover:text-white/70 text-xs mb-6 flex items-center gap-1" data-testid="karmic-new-report-btn">
              <ArrowLeft className="w-3 h-3" /> Back to overview
            </button>
            <KarmicReportView report={report} onDownload={downloadPdf} downloading={downloading} />
          </motion.div>
        ) : generating ? (
          <div className="text-center py-24" data-testid="karmic-generating">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-white/60 text-sm">Calculating your nodal axis and composing your report…</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-3">
              <Orbit className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm tracking-[0.2em] uppercase">Karmic Timeline</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-light text-white/95 mb-3" data-testid="karmic-title">Karmic Timeline</h1>
            <p className="text-white/50 text-base mb-8 max-w-xl">Understand your current chapter—and what's unfolding next. A personalized, rolling 18-month overview of your developmental themes using tropical Vedic astrology.</p>

            {/* Usage + CTA */}
            <div className="rounded-xl p-6 mb-6" style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.06) 0%, rgba(255,255,255,0.03) 100%)", border: "1px solid rgba(251,191,36,0.18)" }} data-testid="karmic-cta-card">
              {usage?.is_premium ? (
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm" data-testid="karmic-usage-line">
                    Premium — {usage.remaining_free} of {usage.monthly_limit} free reports left this month
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-4 h-4 text-white/40" />
                  <span className="text-white/50 text-sm" data-testid="karmic-usage-line">4 free reports each month with Premium. Additional reports ${usage?.price_nonpremium?.toFixed?.(2) ?? "1.99"} each.</span>
                </div>
              )}
              <p className="text-white/40 text-xs mb-4">
                {usage?.is_premium
                  ? <>After your free reports: ${usage?.price_premium_additional?.toFixed?.(2)} each · Resets {fmtDate(usage?.next_reset)} ({usage?.reset_timezone})</>
                  : <>Price: ${usage?.price_nonpremium?.toFixed?.(2)} {usage?.currency} per report</>}
              </p>
              <Button onClick={handleGenerateClick} disabled={generating} className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-11 px-6 text-sm" data-testid="karmic-generate-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                {usage?.can_generate_free ? "Generate Free Report" : `Generate Report — $${(usage?.price_per_report ?? usage?.price_nonpremium ?? 1.99).toFixed(2)}`}
              </Button>
              {!usage?.is_premium && (
                <Link to="/pricing" className="block mt-3 text-amber-400/70 hover:text-amber-400 text-xs" data-testid="karmic-upsell-link">Get Premium for 4 free reports each month →</Link>
              )}
            </div>

            {/* Birth profile summary */}
            <div className="rounded-lg p-4 mb-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="karmic-profile-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-1">Birth Profile</p>
                  <p className="text-white/70 text-sm">Uses your saved Daily Chart profile. The report covers the next 18 months from today.</p>
                </div>
                <Link to="/daily-activation" className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1 flex-shrink-0">Edit <ArrowRight className="w-3 h-3" /></Link>
              </div>
            </div>

            {/* Sample preview */}
            {preview && (
              <div className="rounded-lg p-5 mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)" }} data-testid="karmic-sample-preview">
                <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-3">Sample Preview</p>
                <div className="space-y-3">
                  {Object.entries(preview.sections || {}).map(([k, v]) => (
                    <p key={k} className="text-white/50 text-sm leading-relaxed"><span className="text-amber-400/60">•</span> {v}</p>
                  ))}
                </div>
                <p className="text-white/25 text-xs mt-4">{preview.disclaimer}</p>
              </div>
            )}

            {/* Saved reports */}
            {saved.length > 0 && (
              <div data-testid="karmic-saved-list">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white/60 text-sm tracking-[0.15em] uppercase">Your Saved Reports</h3>
                  <button onClick={refresh} className="text-white/30 hover:text-white/60 text-xs flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                </div>
                <div className="space-y-2">
                  {saved.map((s) => (
                    <button key={s.id} onClick={() => navigate(`/karmic?report=${s.id}`)} className="w-full text-left rounded-lg p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`karmic-saved-${s.id}`}>
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-white/30" />
                        <div>
                          <p className="text-white/80 text-sm">{s.name} · 18-month outlook</p>
                          <p className="text-white/30 text-xs">Generated {fmtDate(s.generated_at)} · free to reopen</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/30" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>

      {/* Review / edit birth info before generating (free path) */}
      <Dialog open={editOpen} onOpenChange={(o) => !o && setEditOpen(false)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="karmic-edit-dialog">
          <DialogHeader><DialogTitle className="text-white/90 font-light text-lg">Confirm your birth details</DialogTitle></DialogHeader>
          <p className="text-white/40 text-xs -mt-1">Review or edit before we calculate your sidereal nodal axis. Changes here aren't saved to your profile.</p>
          <ReportDetailsForm
            packageId="karmic_access_report"
            label="Karmic Timeline"
            initial={prefill}
            onSubmit={(form) => { setEditOpen(false); doGenerate(form); }}
            submitting={generating}
            submitLabel="Generate Report"
          />
        </DialogContent>
      </Dialog>

      {/* Missing birth details (fallback) */}
      <Dialog open={needsDetails} onOpenChange={(o) => !o && setNeedsDetails(false)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="karmic-details-dialog">
          <DialogHeader><DialogTitle className="text-white/90 font-light text-lg">Your birth details</DialogTitle></DialogHeader>
          <p className="text-white/40 text-xs -mt-1">Needed to calculate your nodal axis. Houses are included only when a birth time is given.</p>
          <ReportDetailsForm packageId="karmic_access_report" label="Karmic Timeline" initial={prefill} onSubmit={(form) => doGenerate(form)} submitting={generating} submitLabel="Generate Report" />
        </DialogContent>
      </Dialog>

      {/* Payment required — collect birth details then checkout */}
      <Dialog open={!!checkoutFor} onOpenChange={(o) => !o && setCheckoutFor(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="karmic-checkout-dialog">
          <DialogHeader><DialogTitle className="text-white/90 font-light text-lg">Karmic Timeline — ${(checkoutFor?.price ?? usage?.price_per_report ?? 1.99).toFixed?.(2)}</DialogTitle></DialogHeader>
          <p className="text-white/40 text-xs -mt-1">Enter birth details now so your report is ready right after payment.</p>
          <ReportDetailsForm packageId={checkoutFor?.package_id || "karmic_access_report"} label="Karmic Timeline" initial={prefill} onSubmit={startCheckout} submittingLabel="Redirecting to secure checkout…" submitLabel={`Continue to Payment — $${(checkoutFor?.price ?? usage?.price_per_report ?? 1.99).toFixed?.(2)}`} />
          <p className="text-white/25 text-[11px] text-center">Secured by Stripe · {usage?.currency || "USD"}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
